import { ReactElement, useState } from 'react';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import { ApiRequest } from '@/utils/axios';
import { chatStore } from '@/stores/chat.store';
import useUserStore from '@/stores/user.store';
import { notify } from '@/utils/notify';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';

interface Props {
    children: ReactElement;
    /** Target user (the one the admin would set as winner / shadow). 0 = empty slot. */
    userId: number;
    userName: string;
    /** Required to expose the "Set winner" action. */
    gameType?: 'jackpot' | 'coinflip';
    gameId?: number;
    /** Visual: align popover relative to the avatar. */
    position?: 'right' | 'left' | 'top' | 'bottom';
}

const Item = ({
    onClick,
    icon,
    label,
}: {
    onClick: () => void;
    icon: ReactElement;
    label: string;
}) => (
    <button
        onClick={onClick}
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-fl text-[12.5px] font-medium text-text_2 hover:bg-layer_3 hover:text-text_main transition-colors text-left"
    >
        {icon}
        <span>{label}</span>
    </button>
);

const AdminAvatarMenu = ({
    children,
    userId,
    userName,
    gameType,
    gameId,
    position = 'right',
}: Props) => {
    const profile = useUserStore((s) => s.user);
    const setSendFrom = chatStore((s) => s.setSendFrom);
    const [open, setOpen] = useState(false);

    const isAdmin = profile.status !== 'USER';

    // For non-admins, render children as-is. No popover, no click intercept.
    if (!isAdmin) return children;

    async function setWinner() {
        if (!gameType || !gameId) return;
        const {
            data: { success },
        } = await ApiRequest.post(`/admin/${gameType}/setWinner`, {
            userId,
            gameId,
        });
        if (success) {
            notify(
                'success',
                <SuccessIcon />,
                <NotifyWrapper title="Success" text={`User: ${userName} will win`} />,
                2000
            );
        }
        setOpen(false);
    }

    function setShadow() {
        setSendFrom(userId);
        notify(
            'success',
            <SuccessIcon />,
            <NotifyWrapper title="Success" text={`Now you can chat as ${userName}`} />,
            2000
        );
        setOpen(false);
    }

    return (
        <Popover
            isOpen={open}
            positions={[position]}
            padding={10}
            reposition
            onClickOutside={() => setOpen(false)}
            content={({ position: pos, childRect, popoverRect }) => (
                <ArrowContainer
                    position={pos}
                    childRect={childRect}
                    popoverRect={popoverRect}
                    arrowColor="rgba(20,22,28,0.96)"
                    arrowSize={7}
                    arrowStyle={{ opacity: 1 }}
                    className=""
                    arrowClassName=""
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        className="rounded-fl-md border border-stroke_strong p-1 min-w-[160px]"
                        style={{
                            background: 'rgba(20,22,28,0.96)',
                            backdropFilter: 'blur(20px) saturate(140%)',
                            boxShadow: '0 12px 32px rgba(0,0,0,0.55)',
                        }}
                    >
                        {gameType && gameId !== undefined && (
                            <Item
                                onClick={setWinner}
                                label="Set as winner"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-3.5 h-3.5 stroke-accent_5 fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
                                    >
                                        <path d="M8 21h8M12 17v4M17 4h3v3a4 4 0 0 1-4 4M7 4H4v3a4 4 0 0 0 4 4M7 4v6a5 5 0 0 0 10 0V4H7z" />
                                    </svg>
                                }
                            />
                        )}
                        {userId !== 0 && (
                            <Item
                                onClick={setShadow}
                                label="Chat shadow"
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        className="w-3.5 h-3.5 stroke-accent_3 fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
                                    >
                                        <path d="M12 3a7 7 0 0 0-7 7v9l3-2 2 2 2-2 2 2 3-2v-7a7 7 0 0 0-5-7z" />
                                        <circle cx="9.5" cy="11" r="0.6" fill="currentColor" />
                                        <circle cx="14.5" cy="11" r="0.6" fill="currentColor" />
                                    </svg>
                                }
                            />
                        )}
                    </div>
                </ArrowContainer>
            )}
        >
            <span
                role="button"
                className="cursor-pointer inline-block"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen((v) => !v);
                }}
            >
                {children}
            </span>
        </Popover>
    );
};

export default AdminAvatarMenu;
